import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import {
    APIS,
    SF_CO_CD_INFO,
    ATCH_FILE_VISIBILITY_MAP,
    URLS,
} from "@/tra/tancis/ptli/usr/cocd/services/PtliCoCdService";

import { getNameOfJSDocTypedef } from "typescript";

/*
 * Company Code Details
 * !== 업체 코드 상세 ==!
 */
export const PTLI0407003S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { coTin, coSn } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [isBsopIdVisible, setIsBsopIdVisible] = useState(false);
    const [coTpCd, setCoTpCd] = useState("");

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Company Code Information !== 업체 코드 정보 ==!
        coCdInfo: useForm({
            defaultSchema: SF_CO_CD_INFO,
        }),
    };

    const grid = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 업체 사용자 정보 조회 ==!
        getCoCdInfo: useFetch({
            api: () => APIS.getCoCdInfo(coTin, coSn),
            onSuccess: (data) => {
                const coCdInfo = data.coCdInfo.content;
                form.coCdInfo.setValues(coCdInfo);

                const coTpCd = form.coCdInfo.getValue("coTpCd");
                setIsBsopIdVisible(isAtchFileVisible(coTpCd, "bsopId"));
                setCoTpCd(coTpCd);
                form.coCdInfo.setEditable("bsopId", false);
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Update Company Code !== 업체 코드 수정 ==!
        updateCoCd: useFetch({
            api: (data) => APIS.updateCoCd({ ...data }),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.com.00062", // Successfully saved., !== 정상적으로 저장되었습니다. ==!
                    onConfirm: () => {
                        fetch.getCoCdInfo.fetch();
                    },
                    close: false,
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coCdInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // delete Company Code !== 업체 코드 삭제 ==!
        deleteCoCd: useFetch({
            api: () => APIS.deleteCoCd(coTin, coSn),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00060", // Successfully removed. !== 정상적으로 삭제되었습니다. ==!
                    onCancel: () => {
                        navigate(URLS.ptli0407001q);
                    },
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.coCdInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
            showToast: true,
        }),
    };

    const render = {};

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Save Button !== 저장 버튼 클릭 ==!
        onSaveBtnClick: () => {
            if (!comnUtils.isEmpty(form.coCdInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.coCdInfo.handleSubmit(
                () => {
                    const data = form.coCdInfo.getValues();

                    if (typeof data.bsopId === "object") {
                        data.atchFile = data.bsopId;
                        data.bsopId = null;
                    }

                    modal.openModal({
                        content: "msg.00101", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.updateCoCd.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Delete Company Code !== 업체 코드 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.com.00049", // Confirm remove? !== 삭제하시겠습니까? ==!
                onConfirm: () => {
                    fetch.deleteCoCd.fetch();
                },
            });
        },
    };

    useEffect(() => {
        fetch.getCoCdInfo.fetch();
        // fetch.getCoTpAplyList.fetch();
    }, []);

    const isAtchFileVisible = (coTpCd: string, field: string) => {
        const visibleFields = ATCH_FILE_VISIBILITY_MAP.get(coTpCd) || new Set();
        return visibleFields.has(field);
    };

    const isLcneUseTpCdSelectTrgt = (coTpCdParam: string = coTpCd) => {
        return "C" === coTpCdParam;
    };
    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.coTpCd} edit={false} />
                                <Group.Control {...form.coCdInfo.schema.cstmOfceCd} edit={false}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.coCd} edit={false}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.tasacCrtfNo} edit={false}></Group.Control>
                            </Group.Row>
                            {/* Company Type Name 추가 */}
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.coTpNm}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.gainCfrmOmsnYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.gaotCfrmOmsnYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.stckCretCtraTpCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.stckClarCtraTpCd}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.autoCriYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.autoCroYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.wrhsDpstDays}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.psnWrhsYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.spcdlWrhsYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.coCdInfo.schema.rskCagYn}></Group.Control>
                                <Group.Control {...form.coCdInfo.schema.useYn}></Group.Control>
                            </Group.Row>
                            {isLcneUseTpCdSelectTrgt() && (
                                <Group.Row>
                                    <Group.Control {...form.coCdInfo.schema.lcneUseTpCd}></Group.Control>
                                </Group.Row>
                            )}
                            {isBsopIdVisible && (
                                <Group.Row>
                                    <Group.Control {...form.coCdInfo.schema.bsopId} controlSize={10} />
                                </Group.Row>
                            )}
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0407001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="save"
                        onClick={() => {
                            handler.onSaveBtnClick();
                        }}
                    ></Button>
                    <Button
                        role="delete"
                        onClick={() => {
                            handler.onDeleteBtnClick();
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
